import type { ConstValues } from '@openx/types/helpers';
import { StatusTone } from '../../utils/statusTonePalette';
import {
  asNonEmptyString,
  asOneOf,
  isUnvalidated,
  normalizeList,
  type ParsedOpenXAgentWidget,
} from '../../utils/widgetPayload';

export const PersonaStatus = {
  MATCHING: 'matching',
  NON_MATCHING: 'non-matching',
} as const;

export type PersonaStatus = ConstValues<typeof PersonaStatus>;

export type Persona = {
  id: string;
  status: PersonaStatus;
  title: string;
  description: string | null;
  reasons: string | null;
};

export type PersonasPayload = {
  id: string;
  personas: Persona[];
};

const REASONS_SEPARATOR = ', ';

// the Targeting Explainer mock (PR #9240) sends payloads without an id, so one is filled in
const DEFAULT_PERSONAS_ID = 'personas';

export const getPersonaTone = (status: PersonaStatus): StatusTone =>
  status === PersonaStatus.MATCHING ? StatusTone.POSITIVE : StatusTone.NEGATIVE;

// the contract shows reasons as one comma separated string; a list is joined the same way.
const normalizeReasons = (value: unknown): string | null => {
  if (!Array.isArray(value)) return asNonEmptyString(value);

  const reasons = normalizeList(value, asNonEmptyString);
  return reasons.length > 0 ? reasons.join(REASONS_SEPARATOR) : null;
};

const normalizePersona = (raw: unknown, fallbackId: string): Persona | null => {
  if (!isUnvalidated<keyof Persona>(raw)) return null;

  const title = asNonEmptyString(raw.title);
  const status = asOneOf(PersonaStatus, raw.status);
  if (title === null || status === null) return null;

  return {
    id: asNonEmptyString(raw.id) ?? fallbackId,
    status,
    title,
    description: asNonEmptyString(raw.description),
    reasons: normalizeReasons(raw.reasons),
  };
};

export const parsePersonasPayload = (raw: unknown): ParsedOpenXAgentWidget<PersonasPayload> => {
  if (!isUnvalidated<keyof PersonasPayload>(raw)) return { ok: false, error: 'Personas payload is not an object.' };

  const id = asNonEmptyString(raw.id) ?? DEFAULT_PERSONAS_ID;

  const personas = normalizeList(raw.personas, (persona, index) => normalizePersona(persona, `${id}-${index}`));
  if (personas.length === 0) return { ok: false, error: `Personas widget "${id}" has no usable personas.` };

  return { ok: true, value: { id, personas } };
};
